/**
 * Class that facilitates working with dates
 */

import dayjs from "dayjs";

// Options for formatting dates
const dateOptions: Intl.DateTimeFormatOptions = { day: "2-digit", year: "numeric", month: "2-digit" };

// Local option (formats dates and datetimes into the german format dd.mm.yyyy hh.MM.ss)
const locales = "de-DE";

/**
 * Transforms a sql date in string format to a german date in string format
 * @param sqlString A sql date in string format
 * @returns The sql date as a german date in string format
 */
export const transformSQLStringToGermanDate = (sqlString: string | null) => {
  if (sqlString === null) {
    return "";
  }
  return new Date(sqlString).toLocaleDateString(locales, dateOptions);
};

/**
 * Checks if two dates are the same day
 * @param date1 - The first date to compare
 * @param date2 - The second date to compare
 * @returns True if the dates are the same day, false otherwise
 */
export const isSameDay = (date1: Date, date2: Date) => {
  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  );
};
/**
 * Transforms a string or date to a german string (dd.mm.yyyy)
 */
export const transformToGermanDate = (date: Date | string | null): string => {
  if (date instanceof Date && !isNaN(date.getTime())) {
    return `${String(date.getDate()).padStart(2, "0")}.${String(date.getMonth() + 1).padStart(
      2,
      "0"
    )}.${date.getFullYear()}`;
  }
  if (date && !isNaN(new Date(date).getTime())) {
    const newDate = new Date(date);
    return `${String(newDate.getDate()).padStart(2, "0")}.${String(newDate.getMonth() + 1).padStart(
      2,
      "0"
    )}.${newDate.getFullYear()}`;
  }

  // If 'date' is not a valid Date, return 'Ungültiges Datum'
  return "Ungültiges Datum";
};

/**
 * Formats a date to the german format dd.mm.yyyy
 * @param date - The date to format
 * @returns The formatted date as a string of empty string if the date is null or undefined
 */
export const formatDate = (date: Date | string | null | undefined): string => {
  if (date === null || date === undefined) {
    return "";
  }

  const parsedDate = dayjs(date);

  if (!parsedDate.isValid()) {
    return "";
  }

  return parsedDate.format("DD.MM.YYYY");
};

export const isBeforeToday = (date: dayjs.Dayjs | Date | null | undefined): boolean => {
  if (!date) {
    return false;
  }

  return dayjs(date).startOf("day").isBefore(dayjs().startOf("day"));
};

export const hasDayPassed = (date: dayjs.Dayjs | Date | null | undefined): boolean => {
  if (!date) {
    return false;
  }

  return dayjs(date).isBefore(dayjs());
};
